/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   Hook to get a calendar data structure
 */

import addDays from 'date-fns/addDays'
import eachDayOfInterval from 'date-fns/eachDayOfInterval'
import eachMonthOfInterval from 'date-fns/eachMonthOfInterval'
import eachWeekOfInterval from 'date-fns/eachWeekOfInterval'
import endOfISOWeek from 'date-fns/endOfISOWeek'
import endOfMonth from 'date-fns/endOfMonth'
import format from 'date-fns/format'
import isSameMonth from 'date-fns/isSameMonth'
import startOfISOWeek from 'date-fns/startOfISOWeek'
import startOfMonth from 'date-fns/startOfMonth'
import { useMemo } from 'react'

import { WeekStartsOn } from '../types'

export const useCalendarMatrix = (start: Date, end: Date = start, weekStartsOn: WeekStartsOn = 0) =>
  useMemo(() => calendarMatrix(start, end, weekStartsOn), [start, end, weekStartsOn])

export const calendarMatrix = (start: Date, end: Date, weekStartsOn: WeekStartsOn) => {
  const monthRange = eachMonthOfInterval({
    start,
    end,
  })

  return monthRange.map((date) => {
    const eachWeek = eachWeekOfInterval(
      {
        start: startOfMonth(date),
        end: endOfMonth(date),
      },
      { weekStartsOn }
    )

    const weeks = eachWeek.map((weekDay) => {
      const start = weekStartsOn === 0 ? endOfISOWeek(weekDay) : startOfISOWeek(weekDay)
      const days = eachDayOfInterval({
        start,
        end: addDays(start, 6),
      })

      return days.map((day) => (isSameMonth(date, day) ? day : undefined))
    })

    return {
      month: format(date, 'LLLL'),
      year: format(date, 'yyyy'),
      weeks,
    }
  })
}
